import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Lock, Megaphone, ShieldAlert, UsersRound } from "lucide-react";
import { getCurrentUser } from "aws-amplify/auth";
import Button from "../../components/primitives/button";
import ModerationWorkspace from "./moderation-workspace";
import MarketingWorkspace from "./marketing-workspace";
import {
  STAFF_ROLE_OPTIONS,
  getConsoleAccessContext,
  listGovernanceActions,
  listModerationActions,
  listStaffUsers,
  searchPlatformUsers,
  setUserStaffRole,
} from "../../api/reporting";
import "./ops-console.css";

function rolePillClass(role) {
  const normalized = String(role || "").toUpperCase();
  return `opsConsole__role opsConsole__role--${normalized.toLowerCase()}`;
}

function getDisplayName(entry) {
  const fullName = String(entry?.fullName || "").trim();
  if (fullName) return fullName;
  return "Unnamed user";
}

function toGovernanceErrorMessage(error, fallbackMessage) {
  const message = String(error?.message || "");
  if (message.includes("Forbidden")) return "You do not have permission to perform this action.";
  if (message.includes("MFA")) return "MFA verification is required. Please sign in again and retry.";
  if (message.includes("last SuperAdmin")) return "This action would remove the final SuperAdmin and is blocked.";
  if (message.includes("Too many role changes")) return "Rate limit reached. Please wait before making more role changes.";
  if (message.includes("dual approval")) return "This high-risk role change requires dual approval by policy.";
  if (message.includes("reason is required")) return "Add a reason before submitting role changes.";
  return message || fallbackMessage;
}

function formatGovernanceAction(actionType) {
  const raw = String(actionType || "").trim();
  if (!raw) return "Unknown action";
  return raw
    .toLowerCase()
    .split("_")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function isRoleChangeAction(actionType) {
  const normalized = String(actionType || "").trim().toUpperCase();
  return normalized === "STAFF_ROLE_GRANTED" || normalized === "STAFF_ROLE_REVOKED";
}

function canMutateRoleForTarget({ role, context, isSelf }) {
  if (isSelf) return false;
  const normalized = String(role || "").toUpperCase();
  const isSuperAdmin = Boolean(context?.isSuperAdmin);
  const canManageRoles = Boolean(context?.canManageRoles || context?.isAdmin || isSuperAdmin);

  if (normalized === "SUPERADMIN") return Boolean(context?.canAssignSuperAdmin || isSuperAdmin);
  if (normalized === "ADMIN") return Boolean(context?.canAssignAdmin || isSuperAdmin);
  if (normalized === "MODERATOR" || normalized === "MARKETING") {
    return canManageRoles;
  }
  return false;
}

function StaffRoleActions({
  userId,
  currentRoles,
  context,
  onMutate,
  actionLoading,
  isSelf,
  targetLabel,
}) {
  const normalizedRoles = new Set((currentRoles || []).map((role) => String(role || "").toUpperCase()));
  const actionableRoles = STAFF_ROLE_OPTIONS.filter((role) =>
    canMutateRoleForTarget({
      role,
      context,
      isSelf,
    })
  );

  if (!actionableRoles.length) {
    return <span className="opsConsole__muted">{isSelf ? "Current user" : "No available actions"}</span>;
  }

  return (
    <div className="opsConsole__roleActions">
      {actionableRoles.map((role) => {
        const hasRole = normalizedRoles.has(role);
        const actionKey = `${userId}:${role}:${hasRole ? "remove" : "add"}`;

        return (
          <Button
            key={actionKey}
            variant={hasRole ? "outline" : "primary"}
            disabled={actionLoading === actionKey}
            onClick={() => onMutate({
              userId,
              role,
              enabled: !hasRole,
              actionKey,
              targetLabel,
            })}
          >
            {actionLoading === actionKey
              ? "Saving..."
              : hasRole
                ? `Remove ${role}`
                : `Add ${role}`}
          </Button>
        );
      })}
    </div>
  );
}

export default function OpsConsole() {
  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState(null);
  const [error, setError] = useState("");
  const [staffUsers, setStaffUsers] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [lookupUserId, setLookupUserId] = useState("");
  const [activeModule, setActiveModule] = useState("MODERATION");
  const [moduleHint, setModuleHint] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchError, setUserSearchError] = useState("");
  const [selectedAddAction, setSelectedAddAction] = useState(null);
  const [governanceReason, setGovernanceReason] = useState("");
  const [governanceActions, setGovernanceActions] = useState([]);
  const [governanceActionsLoading, setGovernanceActionsLoading] = useState(false);
  const [moderationActions, setModerationActions] = useState([]);
  const [moderationActionsLoading, setModerationActionsLoading] = useState(false);
  const [reasonTouched, setReasonTouched] = useState(false);

  const loadStaffDirectory = useCallback(async () => {
    setStaffLoading(true);
    setError("");
    try {
      const users = await listStaffUsers({ limit: 100 });
      setStaffUsers(users);
    } catch (staffError) {
      console.error("Failed to load staff users:", staffError);
      setError(toGovernanceErrorMessage(staffError, "Failed to load staff directory."));
      setStaffUsers([]);
    } finally {
      setStaffLoading(false);
    }
  }, []);

  const loadGovernanceAudit = useCallback(async () => {
    setGovernanceActionsLoading(true);
    try {
      const actions = await listGovernanceActions({ limit: 30 });
      setGovernanceActions((actions || []).filter((entry) => isRoleChangeAction(entry?.actionType)));
    } catch (auditError) {
      console.error("Failed to load governance actions:", auditError);
      setError(toGovernanceErrorMessage(auditError, "Failed to load governance audit trail."));
      setGovernanceActions([]);
    } finally {
      setGovernanceActionsLoading(false);
    }
  }, []);

  const loadModerationAudit = useCallback(async () => {
    setModerationActionsLoading(true);
    try {
      const actions = await listModerationActions({ limit: 50 });
      setModerationActions(actions || []);
    } catch (auditError) {
      console.error("Failed to load moderation actions:", auditError);
      setError(toGovernanceErrorMessage(auditError, "Failed to load moderation audit trail."));
      setModerationActions([]);
    } finally {
      setModerationActionsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      setLoading(true);
      setError("");
      try {
        const nextContext = await getConsoleAccessContext();
        if (cancelled) return;

        setContext(nextContext);

        if (nextContext?.canManageRoles && nextContext?.mfaAuthenticated) {
          await loadStaffDirectory();
          await loadGovernanceAudit();
          await loadModerationAudit();
        }
      } catch (bootstrapError) {
        console.error("Failed to load console access:", bootstrapError);
        if (!cancelled) {
          setError(bootstrapError?.message || "Unable to load operations console.");
          setContext(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [loadGovernanceAudit, loadModerationAudit, loadStaffDirectory]);

  useEffect(() => {
    let cancelled = false;
    async function loadCurrentUser() {
      try {
        const user = await getCurrentUser();
        if (cancelled) return;
        setCurrentUserId(String(user?.userId || "").trim());
      } catch {
        if (!cancelled) {
          setCurrentUserId("");
        }
      }
    }
    loadCurrentUser();
    return () => {
      cancelled = true;
    };
  }, []);

  const runRoleMutation = async ({ userId, role, enabled, actionKey, targetLabel }) => {
    if (!userId || !role) return false;
    const reason = String(governanceReason || "").trim();
    if (reason.length < 8) {
      setReasonTouched(true);
      setError("Please provide a short reason (at least 8 characters) before submitting role changes.");
      return false;
    }

    const actionVerb = enabled ? "grant" : "revoke";
    const confirmationMessage = `Confirm ${actionVerb} ${role} for ${targetLabel || "this user"}?`;
    if (!window.confirm(confirmationMessage)) return false;

    setActionLoading(actionKey);
    setError("");

    try {
      await setUserStaffRole({ userId, role, enabled, reason });
      await loadStaffDirectory();
      await loadGovernanceAudit();
      setLookupUserId("");
      setUserSearchResults((prev) => prev.map((entry) => {
        if (String(entry?.userId || "").trim() !== String(userId || "").trim()) return entry;
        const roles = new Set((entry?.roles || []).map((item) => String(item || "").toUpperCase()));
        if (enabled) roles.add(role);
        if (!enabled) roles.delete(role);
        return {
          ...entry,
          roles: Array.from(roles),
        };
      }));
      return true;
    } catch (mutationError) {
      console.error("Failed to update staff role:", mutationError);
      setError(toGovernanceErrorMessage(mutationError, "Failed to update staff role."));
      return false;
    } finally {
      setActionLoading("");
    }
  };

  const runUserSearch = async () => {
    const trimmed = String(userSearchTerm || "").trim();
    if (trimmed.length < 6 || !trimmed.includes("@")) {
      setUserSearchError("Enter a valid email address to search.");
      setUserSearchResults([]);
      return;
    }

    setUserSearchLoading(true);
    setUserSearchError("");
    try {
      const results = await searchPlatformUsers({ query: trimmed, limit: 15 });
      setUserSearchResults(results);
    } catch (searchError) {
      console.error("Failed to search users:", searchError);
      setUserSearchError(toGovernanceErrorMessage(searchError, "Failed to search users."));
      setUserSearchResults([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const runSelectedAddAction = async () => {
    if (!selectedAddAction) return;
    setReasonTouched(true);
    const success = await runRoleMutation({
      userId: selectedAddAction.userId,
      role: selectedAddAction.role,
      enabled: true,
      actionKey: selectedAddActionKey,
      targetLabel: selectedAddAction.targetLabel,
    });
    if (success) {
      setSelectedAddAction(null);
    }
  };

  const isStaff = Boolean(context?.isStaff);
  const hasMfa = Boolean(context?.mfaAuthenticated);

  const filteredUsers = useMemo(() => {
    const needle = String(lookupUserId || "").trim().toLowerCase();
    if (!needle) return staffUsers;

    return staffUsers.filter((entry) => {
      const byName = String(entry?.fullName || "").toLowerCase();
      const byEmail = String(entry?.email || "").toLowerCase();
      const byUsername = String(entry?.username || "").toLowerCase();
      return byName.includes(needle) || byEmail.includes(needle) || byUsername.includes(needle);
    });
  }, [lookupUserId, staffUsers]);

  const modules = useMemo(() => {
    const canModeration = Boolean(context?.isModerator || context?.isAdmin || context?.isSuperAdmin);
    const canMarketing = Boolean(context?.isMarketing || context?.isAdmin || context?.isSuperAdmin);
    const canGovernance = Boolean(context?.isAdmin || context?.isSuperAdmin);
    return [
      {
        key: "MODERATION",
        label: "Moderation",
        icon: ShieldAlert,
        unlocked: canModeration,
        lockHint: "Requires Moderator, Admin, or SuperAdmin.",
        description: "Review reports and take action.",
      },
      {
        key: "MARKETING",
        label: "Marketing",
        icon: Megaphone,
        unlocked: canMarketing,
        lockHint: "Requires Marketing, Admin, or SuperAdmin.",
        description: "Audience insights and campaign delivery.",
      },
      {
        key: "GOVERNANCE",
        label: "Access Governance",
        icon: UsersRound,
        unlocked: canGovernance,
        lockHint: "Requires Admin or SuperAdmin.",
        description: "Manage staff role access.",
      },
    ];
  }, [context]);

  useEffect(() => {
    const moduleKeys = new Set(modules.map((entry) => entry.key));
    const current = modules.find((entry) => entry.key === activeModule);
    if (current?.unlocked) return;

    const nextUnlocked = modules.find((entry) => entry.unlocked);
    if (nextUnlocked) {
      setActiveModule(nextUnlocked.key);
      return;
    }

    if (!moduleKeys.has(activeModule)) {
      setActiveModule("MODERATION");
    }
  }, [activeModule, modules]);

  const normalizedReason = String(governanceReason || "").trim();
  const hasValidReason = normalizedReason.length >= 8;
  const showReasonError = reasonTouched && !hasValidReason;
  const selectedAddActionKey = selectedAddAction
    ? `${selectedAddAction.userId}:${selectedAddAction.role}:add`
    : "";
  const canSubmitSelectedAddAction =
    Boolean(selectedAddAction) && hasValidReason && actionLoading !== selectedAddActionKey;

  useEffect(() => {
    if (!selectedAddAction) return;

    const selectedUser = userSearchResults.find(
      (entry) => String(entry?.userId || "").trim() === String(selectedAddAction.userId || "").trim()
    );
    if (!selectedUser) {
      setSelectedAddAction(null);
      return;
    }

    const roles = new Set((selectedUser?.roles || []).map((role) => String(role || "").toUpperCase()));
    if (roles.has(String(selectedAddAction.role || "").toUpperCase())) {
      setSelectedAddAction(null);
    }
  }, [selectedAddAction, userSearchResults]);

  if (loading) {
    return (
      <div className="opsConsole">
        <div className="opsConsole__status">Loading console...</div>
      </div>
    );
  }

  if (!isStaff) {
    return (
      <div className="opsConsole">
        <div className="opsConsole__notice opsConsole__notice--blocked">
          <h1>Staff Access Required</h1>
          <p>Only staff can use this console.</p>
        </div>
      </div>
    );
  }

  if (!hasMfa) {
    return (
      <div className="opsConsole">
        <div className="opsConsole__notice opsConsole__notice--blocked">
          <h1>MFA Required</h1>
          <p>Sign in again with MFA to continue.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="opsConsole">
      <div className="opsConsole__container">
        <header className="opsConsole__header">
          <div className="opsConsole__headerTop">
            <h1>Operations Console</h1>
            {context && (
              <span className={`opsConsole__accessBadge opsConsole__accessBadge--${
                context.isSuperAdmin ? "superadmin" :
                context.isAdmin ? "admin" :
                context.isModerator ? "moderator" :
                context.isMarketing ? "marketing" : "staff"
              }`}>
                {context.isSuperAdmin ? "SuperAdmin" :
                 context.isAdmin ? "Admin" :
                 context.isModerator && context.isMarketing ? "Moderator + Marketing" :
                 context.isModerator ? "Moderator" :
                 context.isMarketing ? "Marketing" : "Staff"}
              </span>
            )}
          </div>
          <p>Role-based tools for moderation, campaigns, and staff access.</p>
        </header>

        {error ? <div className="opsConsole__error">{error}</div> : null}
        {moduleHint ? <div className="opsConsole__hint">{moduleHint}</div> : null}
        <nav className="opsConsole__moduleNav" aria-label="Console workspaces">
          <div className="opsConsole__moduleTabs" role="tablist" aria-label="Console modules">
            {modules.map((module) => (
              <button
                key={module.key}
                type="button"
                role="tab"
                aria-selected={activeModule === module.key}
                aria-disabled={!module.unlocked}
                disabled={!module.unlocked}
                title={!module.unlocked ? module.lockHint : module.description}
                className={`opsConsole__moduleTab ${
                  activeModule === module.key ? "opsConsole__moduleTab--active" : ""
                } ${!module.unlocked ? "opsConsole__moduleTab--locked" : ""}`}
                onClick={() => {
                  if (!module.unlocked) {
                    setModuleHint(module.lockHint);
                    return;
                  }
                  setModuleHint("");
                  setActiveModule(module.key);
                }}
              >
                <module.icon size={15} aria-hidden="true" />
                <span>{module.label}</span>
                {!module.unlocked ? <Lock size={13} aria-hidden="true" className="opsConsole__moduleTabLock" /> : null}
              </button>
            ))}
          </div>
        </nav>

        {activeModule === "MODERATION" ? (
          <ModerationWorkspace
            context={context}
            currentUserId={currentUserId}
            staffUsers={staffUsers}
          />
        ) : null}

        {activeModule === "MARKETING" ? (
          <MarketingWorkspace
            context={context}
            currentUserId={currentUserId}
          />
        ) : null}

        {activeModule === "GOVERNANCE" && context?.canManageRoles ? (
          <section className="opsConsole__section">
            <div className="opsConsole__sectionHead">
              <h2>Staff Roles</h2>
              <div className="opsConsole__sectionActions">
                <Button variant="outline" onClick={loadStaffDirectory} disabled={staffLoading}>
                  {staffLoading ? "Refreshing..." : "Refresh"}
                </Button>
              </div>
            </div>

            <input
              className="opsConsole__search"
              type="text"
              placeholder="Filter by name or email..."
              value={lookupUserId}
              onChange={(event) => setLookupUserId(event.target.value)}
            />

            <div className="opsConsole__tableWrap">
              <table className="opsConsole__table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Roles</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((entry) => (
                    <tr key={entry.userId || entry.username}>
                      <td>
                        <div className="opsConsole__userName">{getDisplayName(entry)}</div>
                        <div className="opsConsole__userMeta">{entry.email || "N/A"}</div>
                      </td>
                      <td>
                        <div className="opsConsole__roles">
                          {(entry.roles || []).length ? (
                            entry.roles.map((role) => (
                              <span key={`${entry.userId}-${role}`} className={rolePillClass(role)}>
                                {role}
                              </span>
                            ))
                          ) : (
                            <span className="opsConsole__role opsConsole__role--none">None</span>
                          )}
                        </div>
                      </td>
                      <td>{entry.enabled ? "Enabled" : "Disabled"}</td>
                      <td>
                        <StaffRoleActions
                          userId={entry.userId}
                          currentRoles={entry.roles}
                          context={context}
                          onMutate={runRoleMutation}
                          actionLoading={actionLoading}
                          targetLabel={getDisplayName(entry)}
                          isSelf={Boolean(currentUserId) && String(entry.userId || "").trim() === currentUserId}
                        />
                      </td>
                    </tr>
                  ))}

                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={4} className="opsConsole__status">No staff users found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </section>
        ) : null}

        {activeModule === "GOVERNANCE" && context?.canManageRoles ? (
          <section className="opsConsole__section">
            <h2>Add Staff Role</h2>
            <p className="opsConsole__subtle">
              Find a user by email, pick a role, and apply.
            </p>

            <div className="opsConsole__searchRow">
              <input
                className="opsConsole__search"
                type="text"
                placeholder="Search email..."
                value={userSearchTerm}
                onChange={(event) => setUserSearchTerm(event.target.value)}
              />
              <Button variant="outline" onClick={runUserSearch} disabled={userSearchLoading}>
                {userSearchLoading ? "Searching..." : "Search"}
              </Button>
            </div>

            {userSearchError ? <div className="opsConsole__error">{userSearchError}</div> : null}

            <div className="opsConsole__tableWrap">
              <table className="opsConsole__table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Roles</th>
                    <th>Add</th>
                  </tr>
                </thead>
                <tbody>
                  {userSearchResults.map((entry) => {
                    const entryRoles = new Set((entry?.roles || []).map((role) => String(role || "").toUpperCase()));
                    const isSelf = Boolean(currentUserId) && String(entry?.userId || "").trim() === currentUserId;
                    return (
                      <tr key={`search-${entry.userId || entry.username}`}>
                        <td>
                          <div className="opsConsole__userName">{getDisplayName(entry)}</div>
                          <div className="opsConsole__userMeta">{entry.email || "N/A"}</div>
                        </td>
                        <td>
                          <div className="opsConsole__roles">
                            {Array.from(entryRoles).length ? (
                              Array.from(entryRoles).map((role) => (
                                <span key={`search-role-${entry.userId}-${role}`} className={rolePillClass(role)}>
                                  {role}
                                </span>
                              ))
                            ) : (
                              <span className="opsConsole__role opsConsole__role--none">None</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="opsConsole__roleActions">
                            {canMutateRoleForTarget({ role: "MODERATOR", context, isSelf }) && !entryRoles.has("MODERATOR") ? (
                              <Button
                                variant={
                                  selectedAddAction &&
                                  selectedAddAction.userId === entry.userId &&
                                  selectedAddAction.role === "MODERATOR"
                                    ? "primary"
                                    : "outline"
                                }
                                onClick={() =>
                                  setSelectedAddAction({
                                    userId: entry.userId,
                                    role: "MODERATOR",
                                    targetLabel: getDisplayName(entry),
                                  })
                                }
                              >
                                {selectedAddAction &&
                                selectedAddAction.userId === entry.userId &&
                                selectedAddAction.role === "MODERATOR"
                                  ? "Selected"
                                  : "Select Moderator"}
                              </Button>
                            ) : null}

                            {canMutateRoleForTarget({ role: "MARKETING", context, isSelf }) && !entryRoles.has("MARKETING") ? (
                              <Button
                                variant={
                                  selectedAddAction &&
                                  selectedAddAction.userId === entry.userId &&
                                  selectedAddAction.role === "MARKETING"
                                    ? "primary"
                                    : "outline"
                                }
                                onClick={() =>
                                  setSelectedAddAction({
                                    userId: entry.userId,
                                    role: "MARKETING",
                                    targetLabel: getDisplayName(entry),
                                  })
                                }
                              >
                                {selectedAddAction &&
                                selectedAddAction.userId === entry.userId &&
                                selectedAddAction.role === "MARKETING"
                                  ? "Selected"
                                  : "Select Marketing"}
                              </Button>
                            ) : null}

                            {canMutateRoleForTarget({ role: "ADMIN", context, isSelf }) && !entryRoles.has("ADMIN") ? (
                              <Button
                                variant={
                                  selectedAddAction &&
                                  selectedAddAction.userId === entry.userId &&
                                  selectedAddAction.role === "ADMIN"
                                    ? "primary"
                                    : "outline"
                                }
                                onClick={() =>
                                  setSelectedAddAction({
                                    userId: entry.userId,
                                    role: "ADMIN",
                                    targetLabel: getDisplayName(entry),
                                  })
                                }
                              >
                                {selectedAddAction &&
                                selectedAddAction.userId === entry.userId &&
                                selectedAddAction.role === "ADMIN"
                                  ? "Selected"
                                  : "Select Admin"}
                              </Button>
                            ) : null}

                            {!canMutateRoleForTarget({ role: "MODERATOR", context, isSelf }) &&
                            !canMutateRoleForTarget({ role: "MARKETING", context, isSelf }) &&
                            !canMutateRoleForTarget({ role: "ADMIN", context, isSelf }) ? (
                              <span className="opsConsole__muted">
                                {isSelf ? "Current user" : "No access"}
                              </span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {userSearchResults.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="opsConsole__status">Search for a user to begin.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <div className="opsConsole__submitArea">
              <div className="opsConsole__selectionSummary">
                {selectedAddAction
                  ? `Selected: ${selectedAddAction.targetLabel} -> ${selectedAddAction.role}`
                  : "Select a role to continue."}
              </div>

              <label className="opsConsole__reasonLabel" htmlFor="governance-reason">
                Reason (required, min 8 chars)
              </label>
              <textarea
                id="governance-reason"
                className={`opsConsole__reasonInput ${showReasonError ? "opsConsole__reasonInput--invalid" : ""}`}
                value={governanceReason}
                onBlur={() => setReasonTouched(true)}
                onChange={(event) => {
                  setGovernanceReason(event.target.value);
                  if (!reasonTouched) setReasonTouched(true);
                }}
                placeholder="Example: Added moderator coverage for weekend incidents."
                maxLength={500}
              />
              <div className="opsConsole__reasonFooter">
                <span className={`opsConsole__reasonStatus ${hasValidReason ? "opsConsole__reasonStatus--ok" : ""}`}>
                  {hasValidReason ? "Ready" : "Enter at least 8 characters."}
                </span>
                <span className="opsConsole__reasonCount">{normalizedReason.length}/500</span>
              </div>
              {showReasonError ? (
                <div className="opsConsole__inlineError">Please enter at least 8 characters.</div>
              ) : null}

              <Button
                variant="primary"
                disabled={!canSubmitSelectedAddAction}
                onClick={runSelectedAddAction}
              >
                Apply Role
              </Button>
            </div>
          </section>
        ) : null}

        {activeModule === "GOVERNANCE" && context?.canManageRoles ? (
          <section className="opsConsole__section">
            <div className="opsConsole__sectionHead">
              <h2>Moderation Audit</h2>
              <Button variant="outline" onClick={loadModerationAudit} disabled={moderationActionsLoading}>
                {moderationActionsLoading ? "Loading..." : "Refresh"}
              </Button>
            </div>
            <p className="opsConsole__subtle">Newest first.</p>
            <div className="opsConsole__tableWrap">
              <table className="opsConsole__table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Action</th>
                    <th>Actor</th>
                    <th>Target</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {moderationActions.map((entry) => (
                    <tr key={entry.actionId}>
                      <td>{entry.actedAt ? new Date(entry.actedAt).toLocaleString() : "Unknown"}</td>
                      <td>{formatGovernanceAction(entry.actionType)}</td>
                      <td>{entry.adminUserName || entry.adminUserId || "Unknown actor"}</td>
                      <td>{entry.targetUserName || entry.targetUserId || "Unknown target"}</td>
                      <td>{entry.notes || "N/A"}</td>
                    </tr>
                  ))}
                  {moderationActions.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="opsConsole__status">No moderation actions yet.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        {activeModule === "GOVERNANCE" && context?.canManageRoles ? (
          <section className="opsConsole__section">
            <div className="opsConsole__sectionHead">
              <h2>Role Change Audit</h2>
              <Button variant="outline" onClick={loadGovernanceAudit} disabled={governanceActionsLoading}>
                {governanceActionsLoading ? "Loading..." : "Refresh"}
              </Button>
            </div>
            <p className="opsConsole__subtle">Newest first.</p>
            <div className="opsConsole__tableWrap">
              <table className="opsConsole__table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Action</th>
                    <th>Actor</th>
                    <th>Target</th>
                    <th>Reason / Note</th>
                  </tr>
                </thead>
                <tbody>
                  {governanceActions.map((entry) => (
                    <tr key={entry.actionId}>
                      <td>{entry.actedAt ? new Date(entry.actedAt).toLocaleString() : "Unknown"}</td>
                      <td>{formatGovernanceAction(entry.actionType)}</td>
                      <td>{entry.adminUserName || entry.adminUserId || "Unknown actor"}</td>
                      <td>{entry.targetUserName || entry.targetUserId || "Unknown target"}</td>
                      <td>{entry.notes || "N/A"}</td>
                    </tr>
                  ))}
                  {governanceActions.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="opsConsole__status">No role changes yet.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
